import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthState, User } from '../types';

const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: true,
      login: (token: string, user: User) => {
        // Ensure user data is properly structured
        const normalizedUser = {
          ...user,
          credits: typeof user.credits === 'string' ? parseFloat(user.credits) : user.credits,
          role: user.role // Preserve original role
        };
        
        // Store token in localStorage
        localStorage.setItem('auth-token', token);
        
        // Log auth state in development
        console.log('Auth state updated:', {
          isAuthenticated: true,
          role: normalizedUser.role,
          token: token ? 'Present' : 'Missing'
        });
        
        set({ 
          token, 
          user: normalizedUser, 
          isAuthenticated: true,
          isLoading: false
        });
      },
      logout: () => {
        localStorage.removeItem('auth-token');
        set({ 
          token: null, 
          user: null, 
          isAuthenticated: false,
          isLoading: false
        });
      },
      deductCredits: (amount: number) =>
        set((state) => {
          if (!state.user) return state;
          
          const newCredits = Math.max(0, state.user.credits - amount);
          console.log('Deducting credits:', {
            oldCredits: state.user.credits,
            amount,
            newCredits
          });
          
          return {
            user: {
              ...state.user,
              credits: newCredits
            }
          };
        }),
      setLoading: (loading: boolean) => set({ isLoading: loading }),
    }),
    {
      name: 'auth-storage',
      storage: {
        getItem: (name) => {
          const str = localStorage.getItem(name);
          if (!str) return null;
          
          try {
            const data = JSON.parse(str);
            const token = localStorage.getItem('auth-token');
            
            if (!token) {
              return {
                state: {
                  user: null,
                  token: null,
                  isAuthenticated: false,
                  isLoading: false
                }
              };
            }
            
            // Preserve original user role
            if (data.state?.user) {
              data.state.user.role = data.state.user.role;
            }
            
            return {
              ...data,
              state: {
                ...data.state,
                token,
                isAuthenticated: !!token
              }
            };
          } catch (error) {
            console.error('Error parsing auth state:', error);
            return null;
          }
        },
        setItem: (name, value) => localStorage.setItem(name, JSON.stringify(value)),
        removeItem: (name) => localStorage.removeItem(name),
      },
    }
  )
);

export default useAuthStore;